/**
 * Heuristiques de sélection du micro pour la dictée.
 *
 * Problème réel : des apps (Microsoft Teams, OBS, VB-Cable…) installent des
 * périphériques d'entrée VIRTUELS qui deviennent parfois le « défaut système »
 * mais ne captent aucun son. On les détecte pour ne JAMAIS les choisir
 * automatiquement et préférer un micro physique.
 *
 * Module pur — testable.
 */

/** Motifs de libellés de micros virtuels / non physiques connus. */
const VIRTUAL_MIC_PATTERNS: RegExp[] = [
  /teams/i,
  /virtual/i,
  /\bvb[- ]?(audio|cable)\b/i,
  /voicemeeter/i,
  /\bobs\b/i,
  /blackhole/i,
  /soundflower/i,
  /loopback/i,
  /aggregate/i,
  /stereo mix/i,
  /mixage stéréo/i,
  /\bndi\b/i,
  /wave ?link/i,
  /zoom audio/i,
];

/** Le libellé désigne-t-il vraisemblablement un micro virtuel (non physique) ? */
export function isLikelyVirtualMic(label: string): boolean {
  const l = label.trim();
  if (!l) return false;
  return VIRTUAL_MIC_PATTERNS.some((re) => re.test(l));
}

/** Identifiants « alias » qui suivent le défaut système (à éviter pour fixer un micro). */
const ALIAS_DEVICE_IDS = new Set(['', 'default', 'communications']);

/**
 * Choisit le micro recommandé : 1er périphérique CONCRET (id réel, non alias)
 * dont le libellé n'est PAS virtuel. À défaut, le 1er périphérique concret.
 * Renvoie `null` si la liste n'a pas encore de libellés (avant autorisation).
 */
export function pickRecommendedMic(devices: MediaDeviceInfo[]): string | null {
  const concrete = devices.filter(
    (d) => d.kind === 'audioinput' && d.deviceId && !ALIAS_DEVICE_IDS.has(d.deviceId),
  );
  const physical = concrete.find((d) => d.label && !isLikelyVirtualMic(d.label));
  if (physical) return physical.deviceId;
  // Si on ne distingue rien (pas de libellés), ne force pas — laisse le défaut.
  const labelled = concrete.find((d) => d.label);
  return labelled ? labelled.deviceId : null;
}
